import { router, useForm } from '@inertiajs/react'
import type { FormEvent } from 'react'

import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Label } from '@/components/ui/Label'
import { FieldError } from '@/components/feedback/FieldError'

type PrefectureOption = {
  code: number
  name: string
}

type SchoolFormValues = {
  id?: string
  name: string | null
  prefectureId: number | null
  league: string | null
}

type FormProps = {
  values: SchoolFormValues
  prefectures: PrefectureOption[]
  mode: 'create' | 'edit'
}

export function Form({ values, prefectures, mode }: FormProps) {
  const initialState = {
    name: values.name ?? '',
    prefecture_id: values.prefectureId ?? '',
    league: values.league ?? '',
  }

  const { data, setData, post, put, processing, errors, transform } = useForm(initialState)

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    transform((current) => ({ school: current }))
    if (mode === 'create') {
      post('/admin/schools', { preserveScroll: true })
    } else if (values.id) {
      put(`/admin/schools/${values.id}`, { preserveScroll: true })
    }
  }

  return (
    <Card className="max-w-2xl space-y-6" padding="lg">
      <form onSubmit={submit} className="space-y-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="school-name">高校名</Label>
            <Input
              id="school-name"
              value={data.name}
              onChange={(event) => setData('name', event.target.value)}
              placeholder="高校名を入力してください"
            />
            <FieldError className="mt-2" message={errors.name} />
          </div>

          <div>
            <Label htmlFor="school-prefecture">都道府県</Label>
            <Select
              id="school-prefecture"
              value={data.prefecture_id}
              onChange={(event) => setData('prefecture_id', event.target.value)}
            >
              <option value="">選択してください</option>
              {prefectures.map((pref) => (
                <option key={pref.code} value={pref.code}>
                  {pref.name}
                </option>
              ))}
            </Select>
            <FieldError className="mt-2" message={errors.prefecture_id} />
          </div>

          <div>
            <Label htmlFor="school-league">所属リーグ</Label>
            <Input
              id="school-league"
              value={data.league}
              onChange={(event) => setData('league', event.target.value)}
              placeholder="例：〇〇県リーグ1部"
            />
            <FieldError className="mt-2" message={errors.league} />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="ghost" onClick={() => router.get('/admin/schools')}>
            キャンセル
          </Button>
          <Button type="submit" disabled={processing}>
            {mode === 'create' ? '高校を登録' : '高校情報を更新'}
          </Button>
        </div>
      </form>
    </Card>
  )
}
